import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../services/axiosInstance';
import styles from '../styles/BlogDetail.module.css';
import Sidebar from './Sidebar';
import Loader from '../components/Loader';
import RichTextEditor from '../components/RichTextEditor';
import { Save, X, Image } from 'lucide-react';

const BlogEdit = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // First fetch user data
        const token = localStorage.getItem('token');
        let currentUser = null;
        
        if (token) {
          const userResponse = await axiosInstance.get('/auth/verify-token');
          currentUser = userResponse.data.user;
          setUser(currentUser);
        } else {
          // No token - redirect to login
          setError('Login required to edit blogs');
          return;
        }

        // Then fetch blog data
        const response = await axiosInstance.get(`/blogs/detail/${id}`);
        
        if (response.data.success && response.data.data) {
          const blogData = response.data.data;
          
          // Check if current user is the author
          if (String(currentUser._id) !== String(blogData.user_id)) {
            setError('Not authorized to edit this blog');
            return;
          }
          
          setBlog(blogData);
          setTitle(blogData.title);
          setContent(blogData.content);
          
          if (blogData.image) {
            setImagePreview(`${axiosInstance.defaults.baseURL}${blogData.image}`);
          }
        } else {
          setError('Blog not found');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error.response?.data?.message || 'Error loading blog');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || !content.trim()) {
      alert('Title and content are required');
      return;
    }
    
    try {
      setSaving(true);
      
      const formData = new FormData();
      formData.append('title', title);
      formData.append('content', content);
      if (image) {
        formData.append('image', image);
      }
      
      const response = await axiosInstance.put(`/blogs/edit/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      if (response.data.success) {
        alert('Blog updated successfully');
        navigate(`/${id}/${title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`);
      }
    } catch (error) {
      console.error('Error updating blog:', error);
      alert('Failed to update blog');
    } finally {
      setSaving(false);
    }
  };

  const getNavItems = (user) => {
    if (!user || user.name === 'Guest') {
      return [{ name: 'Home', icon: 'Home', path: '/' }];
    }
    return [
      { name: 'Dashboard', icon: 'Home', path: '/dashboard' },
      { name: 'Analytics', icon: 'LineChart', path: '/analytics' },
      { name: 'community Threads & Blogs', icon: 'MessagesSquare', path: '/threads-blogs' },
      { name: 'Feedback', icon: 'MessageSquare', path: '/feedback' }
    ];
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/login');
  };

  if (loading) {
    return <Loader />;
  }

  if (error || !blog) {
    return (
      <div className={styles.pageContainer}>
        <Sidebar 
          user={user || { name: 'Guest' }}
          onLogout={handleLogout}
          navItems={getNavItems(user)}
        />
        <main className={styles.mainContent}>
          <div className={styles.notFound}>
            <h2 className={styles.notFoundTitle}>
              {error === 'Not authorized to edit this blog' ? 'Unauthorized' : 'Blog Not Found'}
            </h2>
            <p className={styles.notFoundText}>
              {error || "The blog you're trying to edit doesn't exist or has been removed."}
            </p>
            <button className={styles.backButton} onClick={() => navigate(-1)}>
              Go Back
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Replace the current authorization check with this simpler one
  // that just checks if the user is logged in
  if (!user || user.name === 'Guest') {
    return (
      <div className={styles.pageContainer}>
        <Sidebar 
          user={user || { name: 'Guest' }}
          onLogout={handleLogout}
          navItems={getNavItems(user)}
        />
        <main className={styles.mainContent}>
          <div className={styles.notFound}>
            <h2 className={styles.notFoundTitle}>Login Required</h2>
            <p className={styles.notFoundText}>
              You must be logged in to edit blogs. Please log in to continue.
            </p>
            <button className={styles.backButton} onClick={() => navigate('/login')}>
              Log In
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <Sidebar 
        user={user || { name: 'Guest' }}
        onLogout={handleLogout}
        navItems={getNavItems(user)}
      />
      <main className={styles.mainContent}>
        <div className={styles.editContainer}>
          <h1 className={styles.editTitle}>Edit Blog</h1>
          
          <form onSubmit={handleSubmit} className={styles.editForm}>
            <div className={styles.formGroup}>
              <label htmlFor="title">Title</label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={styles.titleInput}
                required
              />
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="content">Content</label>
              <RichTextEditor
                initialValue={content}
                onChange={(value) => setContent(value)}
                className={styles.richTextEditor}
                style={{ minHeight: '300px' }}
              />
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="image">Cover Image</label>
              <div className={styles.imageInputContainer}>
                {imagePreview && (
                  <div className={styles.imagePreview}>
                    <img src={imagePreview} alt="Preview" />
                  </div>
                )}
                <div className={styles.imageInput}>
                  <input
                    type="file"
                    id="image"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                  <label htmlFor="image" className={styles.imageUploadButton}>
                    <Image size={18} /> Change Image
                  </label>
                </div>
              </div>
            </div>
            
            <div className={styles.formActions}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={() => navigate(-1)}
                disabled={saving}
              >
                <X size={18} /> Cancel
              </button>
              <button
                type="submit"
                className={styles.saveButton}
                disabled={saving}
              >
                <Save size={18} /> {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default BlogEdit; 